import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, message, Spin } from 'antd';
import { BetaSchemaForm } from '@ant-design/pro-components';
import type {
  ProFormInstance,
  ProFormColumnsType,
} from '@ant-design/pro-components';
import { FileUploadTrigger, FileList } from '../FileUpload';

interface ModalFormProps<T = any> {
  formSchema: ProFormColumnsType<T>[];
  detailApi?: (params: any) => Promise<any>;
  createApi?: (params: any) => Promise<any>;
  updateApi?: (params: any) => Promise<any>;
  formatParams?: (params: any) => any;
  id?: number | string;
  mode?: 'add' | 'edit' | 'view';
  title?: string | (() => string);
  successText?: string;
  button?: React.ComponentType<{ onClick: () => void }>;
  width?: number;
  modalProps?: React.ComponentProps<typeof Modal>;
  formProps?: React.ComponentProps<typeof BetaSchemaForm>;
}

const ModalForm = <T extends Record<string, any> = any>({
  formSchema,
  detailApi,
  createApi,
  updateApi,
  formatParams,
  id,
  mode = 'add',
  title,
  button,
  width = 600,
  successText = '',
  modalProps,
  formProps,
}: ModalFormProps<T>) => {
  // 状态管理
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, any[]>>({});
  const [processedSchema, setProcessedSchema] = useState<ProFormColumnsType<T>[]>([]);
  const [initialValues, setInitialValues] = useState<any>();

  // Refs
  const formRef = useRef<ProFormInstance>(null);
  const isOpeningRef = useRef(false);

  // 计算属性
  const isView = mode === 'view';
  const Btn = button;

  /* ----------------------------- 工具函数 ----------------------------- */

  // 重置表单状态
  const resetFormState = useCallback(() => {
    isOpeningRef.current = false;
    setDynamicOptions({});
    setProcessedSchema([]);
    setInitialValues(undefined);
    formRef.current?.resetFields();
  }, []);

  // 获取下拉选项
  const fetchOptions = useCallback(async (
      col: ProFormColumnsType<T>,
      dependValues?: any,
  ) => {
    const colEx = col as any;
    if (!colEx.requestOptions || !isOpeningRef.current) return;

    try {
      const options = await colEx.requestOptions(dependValues);
      setDynamicOptions(prev => ({
        ...prev,
        [col.dataIndex as string]: options,
      }));
    } catch (err) {
      console.error(`Options fetch failed: ${col.dataIndex}`, err);
    }
  }, []);

  /* ----------------------------- Schema 处理 ----------------------------- */

  // 展开 dependency 字段并处理特殊类型
  const updateSchema = useCallback((values?: any) => {
    const currentValues = values || formRef.current?.getFieldsValue() || {};

    // 展开 dependency 类型的字段
    const allColumns: ProFormColumnsType<T>[] = [];
    formSchema.forEach(col => {
      const colAny = col as any;
      if (colAny.valueType === 'dependency' && typeof colAny.columns === 'function') {
        allColumns.push(...colAny.columns(currentValues));
      } else {
        allColumns.push(col);
      }
    });

    // 处理动态选项和 upload 类型字段
    const finalSchema = allColumns.map(col => {
      const colAny = col as any;
      const newCol = { ...col };

      // 注入动态选项
      const options = dynamicOptions[col.dataIndex as string];
      if (options) {
        newCol.fieldProps = { ...newCol.fieldProps, options };
      }

      // 处理 upload 类型字段
      if (colAny.valueType === 'upload') {
        const { triggerInLabel, ...restFieldProps } = colAny.fieldProps || {};

        const handleUploadSuccess = (val: any) => {
          const fieldName = colAny.dataIndex as string;
          const currentArr = formRef.current?.getFieldValue(fieldName);
          const prevList = Array.isArray(currentArr)
              ? currentArr
              : currentArr ? [currentArr] : [];

          formRef.current?.setFieldsValue({
            [fieldName]: [...prevList, val],
          });

          updateSchema(formRef.current?.getFieldsValue());
        };

        const triggerNode = (
            <FileUploadTrigger
                {...restFieldProps}
                value={currentValues[colAny.dataIndex as string]}
                onUploadSuccess={handleUploadSuccess}
            />
        );

        newCol.formItemProps = {
          ...newCol.formItemProps,
          className: 'w-full [&_label]:w-full [&_label]:flex [&_label]:items-center',
          label: (
              <div className="flex items-center justify-between w-full">
                <span style={{ fontWeight: 'normal' }}>{colAny.title}</span>
                {!isView && triggerNode}
              </div>
          ),
        };

        newCol.renderFormItem = () => (
            <FileList value={currentValues[col.dataIndex as string]}/>
        );
      }

      return newCol;
    });

    setProcessedSchema(finalSchema);
  }, [formSchema, dynamicOptions, isView]);

  /* ----------------------------- Modal 生命周期 ----------------------------- */

  // 打开 Modal 时初始化数据
  useEffect(() => {
    if (!visible) {
      resetFormState();
      return;
    }

    isOpeningRef.current = true;

    // 获取 requestOptions 数据
    formSchema.forEach(col => {
      if ((col as any).requestOptions) {
        fetchOptions(col);
      }
    });

    // 编辑/查看模式：获取详情数据
    if (mode !== 'add' && detailApi) {
      setLoading(true);
      detailApi({ id })
      .then(res => {
        if (isOpeningRef.current) {
          setInitialValues(res);
          // 延迟设置，确保表单已挂载
          setTimeout(() => {
            if (isOpeningRef.current && formRef.current) {
              formRef.current.setFieldsValue(res);
              updateSchema(res);
            }
          }, 0);
        }
      })
      .finally(() => setLoading(false));
      return;
    }

    // 新增模式：使用 formProps.initialValues
    const addModeInitialValues = formProps?.initialValues;
    setInitialValues(addModeInitialValues);

    // 延迟设置，确保表单已挂载
    setTimeout(() => {
      if (isOpeningRef.current && formRef.current) {
        if (addModeInitialValues) {
          formRef.current.setFieldsValue(addModeInitialValues);
          updateSchema(addModeInitialValues);
        } else {
          updateSchema();
        }
      }
    }, 0);
    // updateSchema 被故意排除，避免无限循环
    // formSchema, detailApi, formProps 来自 props，通常是稳定的
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, id, mode]);

  /* ----------------------------- 事件处理 ----------------------------- */

  // 表单值变化
  const handleValuesChange = useCallback((_: any, allValues: any) => {
    updateSchema(allValues);
  }, [updateSchema]);

  // 表单提交
  const handleFinish = useCallback(async (values: any) => {
    setSubmitting(true);
    const formatted = formatParams ? formatParams(values) : values;

    try {
      if (mode === 'add' && createApi) {
        await createApi(formatted);
        message.success(successText || '新增成功');
      } else if (mode !== 'add' && updateApi) {
        await updateApi({ id, ...formatted });
        message.success(successText || '保存成功');
      }

      // 关闭 Modal 并重置状态
      isOpeningRef.current = false;
      setVisible(false);
      resetFormState();
    } catch (err: any) {
      message.error(err.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  }, [mode, createApi, updateApi, formatParams, successText, id, resetFormState]);

  // Modal 取消
  const handleCancel = useCallback(() => {
    isOpeningRef.current = false;
    setVisible(false);
  }, []);

  // Modal 打开
  const handleOpen = useCallback(() => {
    setVisible(true);
  }, []);

  /* ----------------------------- 计算属性 ----------------------------- */

  // Modal 标题
  const modalTitle = useMemo(() => {
    if (typeof title === 'function') return title();

    const modeMap = {
      add: '新建',
      edit: '编辑',
      view: '查看',
    };

    return typeof title === 'string' ? `${modeMap[mode]}${title}` : modeMap[mode];
  }, [title, mode]);

  // 合并初始值
  const mergedInitialValues = useMemo(
      () => initialValues || formProps?.initialValues,
      [initialValues, formProps?.initialValues]
  );

  /* ----------------------------- 渲染 ----------------------------- */

  return (
      <>
        {Btn && <Btn onClick={handleOpen}/>}

        <Modal
            width={width}
            title={modalTitle}
            open={visible}
            onCancel={handleCancel}
            onOk={() => !isView && formRef.current?.submit()}
            confirmLoading={submitting}
            okButtonProps={{ disabled: isView }}
            destroyOnHidden
            {...modalProps}
        >
          <Spin spinning={loading}>
            <BetaSchemaForm
                key={mergedInitialValues?.id || id || 'create'}
                formRef={formRef}
                layout="vertical"
                columns={processedSchema}
                submitter={false}
                disabled={isView}
                onFinish={handleFinish}
                onValuesChange={handleValuesChange}
                preserve={false}
                {...formProps}
                initialValues={mergedInitialValues}
            />
          </Spin>
        </Modal>
      </>
  );
};

export { ModalForm };
